<template>
  <div id="licensePlateSection" class="columns-container container-spacing">
    <div class="column">
      <div class="change-order-page__header-left section-header">
        <h3>
          {{ i18n.t('/change-order/sections/license-plate') }}
        </h3>
      </div>
      <div class="license-plate-panel">
        <div class="license-plate-details">
          <div class="license-plate">
            <label class="label">
              {{ i18n.t('/change-order/sections/license-plate') }}
            </label>
            <br />
            <span>{{ getOriginalLicensePlate }}</span>
          </div>
          <div class="registrationDate">
            <label class="label">
              {{ i18n.t('/change-order/licence-plate/registration-date') }}
            </label>
            <br />
            <span>{{ getOriginalRegistrationDate }}</span>
          </div>
        </div>

        <div v-if="hasLicensePlates" class="plateTypes">
          <strong>
            {{ i18n.t('/change-order/licence-plate/license-plates') }}
          </strong>
          <div v-if="determineOriginalLicensePlateLabels.yellowPlateLabel">
            <span> 1 x {{ determineOriginalLicensePlateLabels.yellowPlateLabel }} </span>
          </div>
          <div v-if="determineOriginalLicensePlateLabels.whitePlateLabel">
            <span>
              {{ determineOriginalWhiteLicensePlateQuantity }}
              {{ determineOriginalLicensePlateLabels.whitePlateLabel }}
            </span>
          </div>
        </div>
        <div class="taxfree-request">
          <strong>
            {{ i18n.t('/change-order/licence-plate/auto-tax-free') }}
          </strong>
          <span>{{ getOriginalTaxFreeOption }} </span>
        </div>
      </div>
    </div>
    <div class="column">
      <div class="change-order-page__header-right section-header">
        <h3>
          {{ i18n.t('/change-order/sections/license-plate') }}
        </h3>
      </div>
      <div class="license-plate-data-input-container">
        <div v-if="licensePlateDataChanged" class="confirmation-message-container">
          <span class="confirmation-message">
            <i class="fas fa-arrows-rotate"></i>&nbsp;
            <strong>
              {{ i18n.t('/change-order/generic/modified') }}
            </strong>
            &nbsp; {{ i18n.t('/change-order/generic/modified-confirmation') }}
          </span>
        </div>
        <div class="commercialHoldDialog">
          <input
            id="commercialHold"
            v-model="commercialHold"
            type="checkbox"
            :disabled="!canChangeHoldCommercial"
            @change="commercialHoldChange"
          />
          <label for="commercialHold">
            {{ i18n.t('/change-order/licence-plate/commercial-hold') }}
          </label>
          <span v-if="!canChangeHoldCommercial" class="edit-icon">
            <i class="fa-solid fa-lock" :title="canChangeHoldCommercialReason"></i>
          </span>
        </div>
        <div v-if="!editMode">
          <div>
            <strong>
              {{ i18n.t('/change-order/licence-plate/license-plates') }}
            </strong>
            <div>
              {{ formatLicensePlateNumberRequestOptions }}
            </div>
            <div>
              <div>{{ selectedYellowLicensePlateName }}</div>
              <div v-if="selectedWhiteLicensePlateName">
                {{ determineChangedWhiteLicensePlateQuantity }}
                {{ selectedWhiteLicensePlateName }}
              </div>
            </div>
            <div class="taxfree-request">
              <strong>
                {{ i18n.t('/change-order/licence-plate/auto-tax-free') }}
              </strong>
              <span>{{ formatTaxFreeOptions }} </span>
            </div>
          </div>
          <div class="license-plate-edit-button-panel">
            <app-button
              data-qa="enter-edit-mode"
              :title="retrieveLockedReasons"
              @click="toggleEdit"
            >
              <i :class="determineChangedClass"></i>&nbsp;
              {{ i18n.t('/edit button label') }}
            </app-button>
          </div>
        </div>
        <div v-if="editMode">
          <div class="generic-license-plate-request">
            <div>
              <strong>
                {{ i18n.t('/change-order/licence-plate/application') }}
              </strong>
            </div>
            <select
              id="license-plate-request"
              v-model="licensePlateNumberRequested"
              :disabled="commercialHold || !canChangeLicensePlateNumber"
              style="width: 450px"
            >
              <option
                v-for="licensePlateNumberRequestOption in licensePlateNumberRequestOptions"
                :key="licensePlateNumberRequestOption.Id"
                :value="licensePlateNumberRequestOption.Id"
              >
                {{ licensePlateNumberRequestOption.Name }}
              </option>
            </select>
            <span v-if="!canChangeLicensePlateNumber" class="edit-icon">
              <i class="fa-solid fa-lock" :title="canChangeLicensePlateNumberReason"></i>
            </span>
          </div>
          <div v-show="!taxFreeRadioValue" class="generic-license-plate-request">
            <div>
              <strong>
                {{ i18n.t('/change-order/licence-plate/yellow-plates') }}
              </strong>
            </div>
            <select
              id="yellow-license-plate-request"
              v-model="yellowLicensePlateType"
              :disabled="!canChangeLicensePlateTypes"
              style="width: 330px"
            >
              <option
                v-for="yellowLicensePlate in yellowLicensePlates"
                :key="yellowLicensePlate.Id"
                :value="yellowLicensePlate.Id"
              >
                {{ yellowLicensePlate.Name }}
              </option>
            </select>
            <span v-if="!canChangeLicensePlateTypes" class="edit-icon">
              <i class="fa-solid fa-lock" :title="canChangeLicensePlateTypesReason"></i>
            </span>
          </div>
          <div
            v-show="!taxFreeRadioValue && yellowLicensePlateType !== 2"
            class="generic-license-plate-request"
          >
            <div>
              <strong>
                {{ i18n.t('/change-order/licence-plate/white-plates') }}
              </strong>
            </div>
            <select
              id="white-license-plate-request"
              v-model="whiteLicensePlateType"
              style="width: 330px"
              :disabled="!canChangeLicensePlateTypes"
              @change="setQuantityWhiteLicensePlateType"
            >
              <option
                v-for="whiteLicensePlate in whiteLicensePlates"
                :key="whiteLicensePlate.Id"
                :value="whiteLicensePlate.Id"
              >
                {{ whiteLicensePlate.Name }}
              </option>
            </select>
            <i
              v-show="whiteLicensePlateType"
              class="fa-solid fa-xmark"
              style="width: 20px; height: 20px; margin-left: 10px; margin-right: 10px"
            />
            <select
              v-show="whiteLicensePlateType"
              id="white-license-plate-quantity-request"
              v-model="quantityWhiteLicensePlateType"
              :disabled="!whiteLicensePlateType"
            >
              <option
                v-for="number in 9"
                :key="number"
                :value="number"
                :disabled="
                  isQuantityWhiteLicensePlateTypeSelectDisabled || !canChangeLicensePlateTypes
                "
              >
                {{ number }}
              </option>
            </select>
            <span v-if="!canChangeLicensePlateTypes" class="edit-icon">
              <i class="fa-solid fa-lock" :title="canChangeLicensePlateTypesReason"></i>
            </span>
          </div>
          <div class="taxfree-request">
            <form-radio-button-list
              id="taxfree-request-radio-button"
              data-qa="taxfree-request-radio-button"
              title="Auto Taxfree? (geen BPM)"
              :options="taxFreeOptions"
              :model-value="taxFreeRadioValue"
              :display-horizontally="true"
              class="taxfree-request-options"
              @change="changeTaxFreeOption"
            >
            </form-radio-button-list>
          </div>
          <div class="license-plate-confirm-button-panel">
            <app-button data-qa="confirm-license-plate-change" @click="confirmLicensePlateChanges">
              {{ i18n.t('/common/confirm') }}
            </app-button>
            <app-button data-qa="cancel-license-plate-change" @click="cancelEdit">
              {{ i18n.t('/common/cancel') }}
            </app-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import FormRadioButtonList from '@/components/generic/presentational/atoms/formRadioButtonList/FormRadioButtonList.vue'
import ChangeOrderStatus from '@/helpers/enums/changeOrderStatus'

export default {
  components: { FormRadioButtonList, AppButton },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      taxFreeRadioValue: null,
      licensePlateNumberRequested: false,
      yellowLicensePlateType: null,
      whiteLicensePlateType: null,
      quantityWhiteLicensePlateType: null,
      isQuantityWhiteLicensePlateTypeSelectDisabled: false,
      commercialHold: false,
      editMode: false,
    }
  },
  computed: {
    ...mapGetters({
      changeOrder: 'changeOrder/get',
      dealerSettings: 'user/dealerSettings',
    }),
    selectedYellowLicensePlateName() {
      const selectedType = this.yellowLicensePlates.find(
        (plate) => plate.Id === this.yellowLicensePlateType
      )
      let licensePlateName = selectedType ? selectedType.Name : 'Geel standaard'
      // "Geen" means no plate is produced, so the "1 x" quantity prefix would read as "1 of None".
      if (this.yellowLicensePlateType === 2) {
        return licensePlateName
      }
      if (this.yellowLicensePlateType) {
        return '1 x ' + licensePlateName
      }
      return ''
    },
    selectedWhiteLicensePlateName() {
      const selectedType = this.whiteLicensePlates
        .filter((t) => t.Id > 0)
        .find((plate) => plate.Id === this.whiteLicensePlateType)
      return selectedType ? selectedType.Name : ''
    },
    determineChangedWhiteLicensePlateQuantity() {
      let changedQuantityWhiteLicensePlates =
        this.changeOrder.LicensePlates.ChangedQuantityWhiteLicensePlates

      if (changedQuantityWhiteLicensePlates) {
        return changedQuantityWhiteLicensePlates + ' x '
      }
      return ''
    },
    hasLicensePlates() {
      return this.changeOrder.LicensePlates.OriginalLicensePlateType
    },
    determineOriginalLicensePlateLabels() {
      let originalLicensePlateType = this.changeOrder.LicensePlates.OriginalLicensePlateType

      switch (originalLicensePlateType) {
        case 'N':
          return {
            yellowPlateLabel: 'Geel standaard',
            whitePlateLabel: null,
          }
        case 'L':
          return {
            yellowPlateLabel: 'Geel Luxe',
            whitePlateLabel: null,
          }
        case 'W':
          return {
            yellowPlateLabel: 'Geel standaard',
            whitePlateLabel: 'Wit standaard',
          }
        case 'S':
          return {
            yellowPlateLabel: 'Geel Luxe',
            whitePlateLabel: 'Wit standaard',
          }
        case 'X':
          return {
            yellowPlateLabel: 'Geel Luxe',
            whitePlateLabel: 'Wit Luxe',
          }
        case 'G':
          return {
            yellowPlateLabel: 'Geen',
            whitePlateLabel: null,
          }
        default:
          return {
            yellowPlateLabel: null,
            whitePlateLabel: null,
          }
      }
    },
    determineOriginalWhiteLicensePlateQuantity() {
      let originalQuantityWhiteLicensePlates =
        this.changeOrder.LicensePlates.OriginalQuantityWhiteLicensePlates

      if (originalQuantityWhiteLicensePlates) {
        return originalQuantityWhiteLicensePlates + ' x '
      }
      return ''
    },
    formatLicensePlateNumberRequestOptions() {
      if (this.commercialHold === true) {
        return 'Status: aanvraag geblokkeerd'
      } else {
        return this.licensePlateNumberRequestOptions.find(
          (x) => x.Id === this.licensePlateNumberRequested
        ).Name
      }
    },
    licensePlateNumberRequestOptions() {
      return [
        {
          Id: false,
          Name: 'Geen kenteken aanvraag en financiële afhandeling',
        },
        {
          Id: true,
          Name: 'Start aanvraag kenteken en financiële afhandeling',
        },
      ]
    },
    yellowLicensePlates() {
      return [
        {
          Id: 0,
          Name: 'Geel standaard',
        },
        {
          Id: 1,
          Name: 'Geel luxe',
        },
        {
          Id: 2,
          Name: 'Geen',
        },
      ]
    },
    whiteLicensePlates() {
      return [
        {
          Id: 0,
          Name: 'Geen',
        },
        {
          Id: 1,
          Name: 'Wit standaard',
        },
      ]
    },
    determineYellowLicensePlateSelected() {
      let originalLicensePlateType = this.changeOrder.LicensePlates.ChangedLicensePlateType

      switch (originalLicensePlateType) {
        case 'G':
          return 2 //geen platen
        case 'N':
          return 0 //normal yellow
        case 'L':
          return 1 //luxury yellow
        case 'W':
          return 0 //normal yellow
        case 'S':
          return 1 //luxury yellow
        case 'X':
          return 1 //luxury yellow
        default:
          return null
      }
    },

    determineWhiteLicensePlateSelected() {
      let originalLicensePlateType = this.changeOrder.LicensePlates.ChangedLicensePlateType
      switch (originalLicensePlateType) {
        case 'G':
          return 0 //geen platen — no white
        case 'N':
          return 0 //no white plates
        case 'L':
          return 0 //no white plates
        case 'W':
          return 1 //normal white plates
        case 'S':
          return 1 //normal white plates
        default:
          return 0
      }
    },
    determineQuantityWhitePlatesSelected() {
      return this.changeOrder.LicensePlates.ChangedQuantityWhiteLicensePlates
    },
    formatTaxFreeOptions() {
      return this.getTaxFreeOptionLabel(this.taxFreeRadioValue)
    },
    taxFreeOptions() {
      return [
        {
          name: 'Nee',
          value: false,
        },
        {
          name: 'Ja',
          value: true,
        },
      ]
    },
    determineLicensePlateType() {
      if (this.yellowLicensePlateType === 2) {
        return 'G' //geen platen
      }
      if (this.yellowLicensePlateType === 0 && this.whiteLicensePlateType === 0) {
        return 'N' //normal yellow and no white plates
      }
      if (this.yellowLicensePlateType === 0 && this.whiteLicensePlateType === 1) {
        return 'W' //normal yellow and normal white plates
      }
      if (this.yellowLicensePlateType === 1 && this.whiteLicensePlateType === 0) {
        return 'L' //luxury yellow and no white plates
      }
      if (this.yellowLicensePlateType === 1 && this.whiteLicensePlateType === 1) {
        return 'S' //luxury yellow and normal white plates
      }
      return null
    },
    licensePlateDataChanged() {
      return (
        this.commercialHoldHasChanged ||
        this.changeOrder.LicensePlates.LicensePlateNumberRequested === true ||
        this.licensePlateTypeHasChanged ||
        this.taxFreeHasChanged
      )
    },
    commercialHoldHasChanged() {
      let originalCommercialHold = this.changeOrder.Holds.CommercialHold.OriginalCommercialHold
      let changedCommercialHold = this.changeOrder.Holds.CommercialHold.ChangedCommercialHold

      return originalCommercialHold !== changedCommercialHold
    },
    licensePlateTypeHasChanged() {
      return (
        this.changeOrder.LicensePlates.OriginalLicensePlateType !==
        this.changeOrder.LicensePlates.ChangedLicensePlateType
      )
    },
    taxFreeHasChanged() {
      return (
        this.changeOrder.TaxFree.OriginalTaxFreeRequest !==
        this.changeOrder.TaxFree.ChangedTaxFreeRequest
      )
    },
    getOriginalLicensePlate() {
      let originalCommercialHold = this.changeOrder.Holds.CommercialHold.OriginalCommercialHold
      if (originalCommercialHold === true) {
        return 'Status: aanvraag geblokkeerd'
      }

      let licensePlate = this.changeOrder.CarConfiguration.LicensePlateNumber
      if (licensePlate) {
        return this.formatLicensePlate(licensePlate)
      } else {
        return 'Geen kenteken bekend'
      }
    },
    getOriginalRegistrationDate() {
      let registrationDate = this.changeOrder.CarConfiguration.RegistrationDate
      if (registrationDate) {
        return registrationDate
      }
      return 'Geen registratie datum bekend'
    },
    getOriginalTaxFreeOption() {
      let originalTaxFreeRequest = this.changeOrder.TaxFree.OriginalTaxFreeRequest
      return this.getTaxFreeOptionLabel(originalTaxFreeRequest)
    },
    canChangeLicensePlateNumber() {
      return this.changeOrder.Permissions?.CanChangeSettlement?.Allowed === true
    },
    canChangeLicensePlateNumberReason() {
      return this.changeOrder.Permissions?.CanChangeSettlement?.Reason
    },
    canChangeLicensePlateTypes() {
      return this.changeOrder.Permissions?.CanChangeLicensePlate?.Allowed === true
    },
    canChangeLicensePlateTypesReason() {
      return this.changeOrder.Permissions?.CanChangeLicensePlate?.Reason
    },
    canChangeLicensePlates() {
      return (
        this.changeOrder.MainOrderStatus !== ChangeOrderStatus.submitted &&
        this.changeOrder.MainOrderStatus !== ChangeOrderStatus.archived &&
        this.changeOrder.LicensePlates?.OrderStatus !== ChangeOrderStatus.submitted &&
        (this.canChangeLicensePlateNumber || this.canChangeLicensePlateTypes)
      )
    },
    determineChangedClass() {
      if (this.canChangeLicensePlates) {
        return 'fa-solid fa-pencil'
      } else {
        return 'fa-solid fa-lock'
      }
    },
    retrieveLockedReasons() {
      let lockedReasons = []
      if (!this.canChangeLicensePlates) {
        if (!this.changeOrder.Permissions?.CanChangeSettlement?.Allowed) {
          lockedReasons.push(this.changeOrder.Permissions?.CanChangeSettlement?.Reason)
        }
        if (!this.changeOrder.Permissions?.CanChangeLicensePlate?.Allowed) {
          lockedReasons.push(this.changeOrder.Permissions?.CanChangeLicensePlate?.Reason)
        }
        if (
          this.changeOrder.LicensePlates?.OrderStatus === ChangeOrderStatus.submitted ||
          this.changeOrder.MainOrderStatus === ChangeOrderStatus.submitted ||
          this.changeOrder.MainOrderStatus === ChangeOrderStatus.archived
        ) {
          lockedReasons.push('Order has been submitted or deleted')
        }
      }
      return lockedReasons.join('\n')
    },
    canChangeHoldCommercial() {
      return (
        this.dealerSettings.isImporter &&
        this.changeOrder.MainOrderStatus !== ChangeOrderStatus.submitted &&
        this.changeOrder.MainOrderStatus !== ChangeOrderStatus.archived &&
        this.changeOrder.Holds?.CommercialHold?.OrderStatus !== ChangeOrderStatus.submitted &&
        this.changeOrder.Permissions?.CanChangeHoldCommercial?.Allowed === true
      )
    },
    canChangeHoldCommercialReason() {
      let lockedReasons = []
      if (!this.canChangeLicensePlates) {
        if (!this.changeOrder.Permissions?.CanChangeHoldCommercial?.Allowed) {
          lockedReasons.push(this.changeOrder.Permissions?.CanChangeHoldCommercial?.Reason)
        }
        if (
          this.changeOrder.LicensePlates?.OrderStatus === ChangeOrderStatus.submitted ||
          this.changeOrder.MainOrderStatus === ChangeOrderStatus.submitted ||
          this.changeOrder.MainOrderStatus === ChangeOrderStatus.archived
        ) {
          lockedReasons.push('Order has been submitted or deleted')
        }
      }
      return lockedReasons.join('\n')
    },
  },
  mounted() {
    this.setLicensePlateTypes()
    this.setTaxFreeValue()
  },
  methods: {
    setLicensePlateTypes() {
      if (this.changeOrder && this.changeOrder.Holds.CommercialHold) {
        this.commercialHold = this.changeOrder.Holds.CommercialHold.ChangedCommercialHold
      }
      if (this.changeOrder && this.changeOrder.LicensePlates) {
        this.yellowLicensePlateType = this.determineYellowLicensePlateSelected
        this.whiteLicensePlateType = this.determineWhiteLicensePlateSelected
        this.quantityWhiteLicensePlateType = this.determineQuantityWhitePlatesSelected
      }
    },
    setTaxFreeValue() {
      if (this.changeOrder?.TaxFree?.ChangedTaxFreeRequest !== null) {
        this.taxFreeRadioValue = this.changeOrder.TaxFree.ChangedTaxFreeRequest
      }
    },
    getTaxFreeOptionLabel(selectedValue) {
      if (selectedValue !== null) {
        return this.taxFreeOptions.find((option) => option.value === selectedValue)?.name
      } else {
        return 'Onbekend'
      }
    },
    toggleEdit() {
      if (this.canChangeLicensePlates) {
        this.editMode = !this.editMode
      }
    },
    setQuantityWhiteLicensePlateType() {
      if (this.whiteLicensePlateType === 0) {
        this.isQuantityWhiteLicensePlateTypeSelectDisabled = true
        this.quantityWhiteLicensePlateType = null
      } else {
        this.isQuantityWhiteLicensePlateTypeSelectDisabled = false
        this.quantityWhiteLicensePlateType = 1
      }
    },
    confirmLicensePlateChanges() {
      this.changeOrder.LicensePlates.ChangedLicensePlateType = this.determineLicensePlateType

      if (!this.commercialHoldHasChanged) {
        this.changeOrder.LicensePlates.LicensePlateNumberRequested =
          this.licensePlateNumberRequested
      }

      if (this.determineLicensePlateType === 'G') {
        this.changeOrder.LicensePlates.ChangedQuantityWhiteLicensePlates = null
        this.whiteLicensePlateType = 0
        this.quantityWhiteLicensePlateType = null
      } else if (this.whiteLicensePlateType > 0) {
        this.changeOrder.LicensePlates.ChangedQuantityWhiteLicensePlates =
          this.quantityWhiteLicensePlateType
      } else {
        this.changeOrder.LicensePlates.ChangedQuantityWhiteLicensePlates = null
        this.quantityWhiteLicensePlateType = null
      }

      if (this.taxFreeRadioValue !== null) {
        this.changeOrder.TaxFree.ChangedTaxFreeRequest = this.taxFreeRadioValue
      }

      this.$store.commit('changeOrder/setLicensePlatesStatus')
      this.editMode = false
    },
    cancelEdit() {
      this.editMode = false
      // Reset form values if needed
    },
    commercialHoldChange() {
      this.changeOrder.Holds.CommercialHold.ChangedCommercialHold = this.commercialHold
      this.$store.commit('changeOrder/setLicensePlatesStatus')
    },
    formatLicensePlate(licensePlate) {
      if (/^[A-Z]{2}-\d{2}-\d{2}$/.test(licensePlate)) {
        // If already in correct format, return as is
        return licensePlate
      } else if (/^\d{2}\d{2}[A-Z]{2}$/.test(licensePlate)) {
        // 2) format license plate 99-99-XX to 99-99-XX
        return `${licensePlate.substring(0, 2)}-${licensePlate.substring(
          2,
          4
        )}-${licensePlate.substring(4)}`
      } else if (/^\d{2}[A-Z]{2}\d{2}$/.test(licensePlate)) {
        // 3) format license plate 99XX99 to 99-XX-99
        return `${licensePlate.substring(0, 2)}-${licensePlate.substring(
          2,
          4
        )}-${licensePlate.substring(4)}`
      } else if (/^[A-Z]{2}\d{2}[A-Z]{2}$/.test(licensePlate)) {
        // 4) format license plate XX99XX to XX-99-XX
        return `${licensePlate.substring(0, 2)}-${licensePlate.substring(
          2,
          4
        )}-${licensePlate.substring(4)}`
      } else if (/^[A-Z]{2}[A-Z]{2}\d{2}$/.test(licensePlate)) {
        // 5) format license plate XXXX99 to XX-XX-99
        return `${licensePlate.substring(0, 2)}-${licensePlate.substring(
          2,
          4
        )}-${licensePlate.substring(4)}`
      } else if (/^\d{2}[A-Z]{2}[A-Z]{2}$/.test(licensePlate)) {
        // 6) format license plate 99XXXX to 99-XX-XX
        return `${licensePlate.substring(0, 2)}-${licensePlate.substring(
          2,
          4
        )}-${licensePlate.substring(4)}`
      } else if (/^\d{2}[A-Z]{3}\d{1}$/.test(licensePlate)) {
        // 7) format license plate 99XXX9 to 99-XXX-9
        return `${licensePlate.substring(0, 2)}-${licensePlate.substring(
          2,
          5
        )}-${licensePlate.substring(5)}`
      } else if (/^\d{1}[A-Z]{3}\d{2}$/.test(licensePlate)) {
        // 8) format license plate 9XXX99 to 9-XXX-99
        return `${licensePlate.substring(0, 1)}-${licensePlate.substring(
          1,
          4
        )}-${licensePlate.substring(4)}`
      } else if (/^[A-Z]{2}\d{3}[A-Z]{1}$/.test(licensePlate)) {
        // 9) format license plate XX999X to XX-999-X
        return `${licensePlate.substring(0, 2)}-${licensePlate.substring(
          2,
          5
        )}-${licensePlate.substring(5)}`
      } else if (/^[A-Z]{1}\d{3}[A-Z]{2}$/.test(licensePlate)) {
        // 10) format license plate X999XX to X-999-XX
        return `${licensePlate.substring(0, 1)}-${licensePlate.substring(
          1,
          4
        )}-${licensePlate.substring(4)}`
      } else if (/^[A-Z]{3}\d{2}[A-Z]{1}$/.test(licensePlate)) {
        // 11) format license plate XXX99X to XXX-99-X
        return `${licensePlate.substring(0, 3)}-${licensePlate.substring(
          3,
          5
        )}-${licensePlate.substring(5)}`
      } else if (/^[A-Z]{1}\d{2}[A-Z]{3}$/.test(licensePlate)) {
        // 12) format license plate X99XXX to X-99-XXX
        return `${licensePlate.substring(0, 1)}-${licensePlate.substring(
          1,
          3
        )}-${licensePlate.substring(3)}`
      } else if (/^\d{1}[A-Z]{2}\d{3}$/.test(licensePlate)) {
        // 13) format license plate 9XX999 to 9-XX-999
        return `${licensePlate.substring(0, 1)}-${licensePlate.substring(
          1,
          3
        )}-${licensePlate.substring(3)}`
      } else if (/^\d{3}[A-Z]{2}\d{1}$/.test(licensePlate)) {
        // 14) format license plate 999XX9 to 999-XX-9
        return `${licensePlate.substring(0, 3)}-${licensePlate.substring(
          3,
          5
        )}-${licensePlate.substring(5)}`
      }
      return licensePlate
    },
    changeTaxFreeOption(event) {
      this.taxFreeRadioValue = event === 'true'

      // reset license plate choices to null because a car
      // won't get license plates when it is tax fee
      this.yellowLicensePlateType = null
      this.whiteLicensePlateType = null
    },
  },
}
</script>
<style lang="scss" scoped>
.columns-container {
  display: flex;
  justify-content: space-between;
}

.container-spacing {
  margin-top: 50px;
}

.column {
  flex: 1;
  margin: 0 20px;
}

h3 {
  font-weight: 400;
  margin: 0;
}

.change-order {
  &-page__header-left {
    h3 {
      color: $black;
    }
  }

  &-page__header-right {
    h3 {
      color: $color-primary;
    }
  }
}

.section-header {
  border-bottom: 1px solid #ffdd9c;
  align-items: center;
  margin-bottom: 1rem;
}

.license-plate-panel {
  margin-top: 20px;
  label {
    font-weight: bold;
    margin-bottom: 2px;
  }
}

.license-plate-details {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  .license-plate,
  .registrationDate {
    flex-basis: 50%;
  }
}

.license-plate-data-input-container {
  margin-top: 20px;
  margin-bottom: 40px;
}

.commercialHoldDialog {
  padding-bottom: 30px;
  border-bottom: 1px solid #ffdd9c;
  input {
    position: relative;
    top: 5px;
    width: 20px;
    height: 20px;
  }
  label {
    margin-left: 10px;
  }
}

.confirmation-message {
  margin-top: 5px;
  background-color: #ffdd9c;
  font-size: 18px;
  padding: 10px;
}

.confirmation-message-container {
  margin-bottom: 15px;
}

.license-plate-confirm-button-panel,
.license-plate-edit-button-panel {
  margin-top: 20px;
}

.generic-license-plate-request {
  padding-top: 15px;
}

.plateTypes {
  margin-top: 20px;
}

.edit-icon {
  margin-left: 10px;
  cursor: pointer;
}

.taxfree-request {
  margin-top: 25px;
}

:deep(.taxfree-request-options) {
  label {
    padding-right: 30px;
  }
}
</style>
